import React from 'react';
import {
  type FormattedInputType,
  cleanDigits,
  applyProgressiveMask,
  getDefaultPlaceholder,
  getMaxLengthForType,
} from '../utils/maskUtils';

export interface FormattedInputProps {
  type: FormattedInputType;
  value: string | null | undefined;
  onChange: (value: string, rawValue?: string) => void;
  returnRaw?: boolean;
  placeholder?: string;
  className?: string;
  required?: boolean;
  id?: string;
  name?: string;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  'data-testid'?: string;
}

export default function FormattedInput({
  type,
  value,
  onChange,
  returnRaw = false,
  placeholder,
  className = 'w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all placeholder:text-gray-400',
  required = false,
  id,
  name,
  onBlur,
  onKeyDown,
  disabled = false,
  autoFocus = false,
  'data-testid': testId,
}: FormattedInputProps) {
  const displayValue = applyProgressiveMask(type, value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawInput = e.target.value;
    const masked = applyProgressiveMask(type, rawInput);
    const raw = cleanDigits(rawInput);

    if (returnRaw) {
      onChange(raw, masked);
    } else {
      onChange(masked, raw);
    }
  };

  return (
    <input
      type="text"
      id={id}
      name={name}
      value={displayValue}
      onChange={handleChange}
      placeholder={placeholder || getDefaultPlaceholder(type)}
      className={className}
      required={required}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      disabled={disabled}
      autoFocus={autoFocus}
      maxLength={getMaxLengthForType(type)}
      data-testid={testId}
    />
  );
}
